import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe, ChevronDown } from 'lucide-react';
import { VideoBackground } from './VideoBackground';
import { useTheme } from '../contexts/ThemeContext';

// Country configuration with flags and labels
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'Canada', name: 'Canada', flag: '🇨🇦' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'Ireland', name: 'Ireland', flag: '🇮🇪' },
  { code: 'Germany', name: 'Germany', flag: '🇩🇪' },
  { code: 'France', name: 'France', flag: '🇫🇷' },
  { code: 'Spain', name: 'Spain', flag: '🇪🇸' },
  { code: 'Italy', name: 'Italy', flag: '🇮🇹' },
  { code: 'Netherlands', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'Belgium', name: 'Belgium', flag: '🇧🇪' },
  { code: 'Poland', name: 'Poland', flag: '🇵🇱' },
  { code: 'Austria', name: 'Austria', flag: '🇦🇹' },
  { code: 'Portugal', name: 'Portugal', flag: '🇵🇹' },
  { code: 'Australia', name: 'Australia', flag: '🇦🇺' },
  { code: 'New Zealand', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'Israel', name: 'Israel', flag: '🇮🇱' },
  { code: 'Brazil', name: 'Brazil', flag: '🇧🇷' },
];

// Region to country mapping
const REGION_COUNTRY_MAP: Record<string, string[]> = {
  'North America': ['US', 'Canada'],
  Europe: [
    'UK',
    'Ireland',
    'Germany',
    'Spain',
    'Austria',
    'Belgium',
    'Bulgaria',
    'Croatia',
    'Cyprus',
    'Czechia',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Greece',
    'Hungary',
    'Iceland',
    'Italy',
    'Latvia',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'Netherlands',
    'Norway',
    'Poland',
    'Portugal',
    'Romania',
    'Slovakia',
    'Slovenia',
    'Sweden',
    'Switzerland',
  ],
  APAC: ['Australia', 'New Zealand'],
  'Middle East': ['Israel'],
  'Latin America': ['Brazil'],
};

type LocationOptions = {
  countries: string[];
  regions?: string[];
};

type LocationSelectorProps = {
  options: LocationOptions;
};

function LocationSelector({ options }: LocationSelectorProps) {
  const navigate = useNavigate();
  const { region } = useParams<{ region?: string }>();
  const { actualTheme } = useTheme();

  // Handle country click - navigate directly to platform selection with country filter
  const handleCountryClick = (country: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent region card click
    }
    // Navigate to browse mode with country filter to show platforms for that country
    navigate(`/browse?country=${country}`);
  };

  // Handle region click - navigate to region view
  const handleRegionClick = (regionName: string) => {
    navigate(`/location/${encodeURIComponent(regionName)}`);
  };

  // Get region emoji
  const getRegionEmoji = (regionName: string) => {
    switch (regionName) {
      case 'North America':
        return '🌎';
      case 'Europe':
        return '🌍';
      case 'APAC':
        return '🌏';
      case 'Middle East':
        return '🏺';
      case 'Latin America':
        return '🌎';
      default:
        return '🌐';
    }
  };

  // If we're on a region page, show countries in that region
  if (region) {
    const decodedRegion = decodeURIComponent(region);
    const countriesInRegion =
      REGION_COUNTRY_MAP[decodedRegion]?.filter(country => options.countries.includes(country)) ||
      [];

    if (countriesInRegion.length === 0) {
      return (
        <div className="min-h-screen relative overflow-hidden">
          <VideoBackground
            icon={Globe}
            title="No Countries Available"
            description={`No products are currently available in ${decodedRegion}`}
            showOverlay={false}
            theme={actualTheme}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-slate-900/20">
            <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
              <div className="max-w-4xl mx-auto text-center">
                <div className="text-center mb-8">
                  <button
                    onClick={() => navigate('/location')}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
                  >
                    <ChevronDown className="w-4 h-4 rotate-90" />
                    Back to regions
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen relative overflow-hidden">
        <VideoBackground
          icon={Globe}
          title={`Choose your country in ${decodedRegion}`}
          description="Select a country to view available solutions"
          showOverlay={false}
          theme={actualTheme}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-slate-900/20 overflow-y-auto">
          <div className="relative z-10 min-h-screen pt-24 pb-8 px-4">
            <div className="max-w-4xl mx-auto">
              <div className="animate-fade-in">
                {/* Back Button - Content Aligned */}
                <div className="mb-6">
                  <button
                    onClick={() => navigate('/location')}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
                  >
                    <ChevronDown className="w-4 h-4 rotate-90" />
                    Back to regions
                  </button>
                </div>

                {/* Country Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                  {countriesInRegion.map(country => {
                    const countryInfo = COUNTRIES.find(c => c.code === country);
                    return (
                      <button
                        key={country}
                        onClick={() => handleCountryClick(country)}
                        className="group relative p-4 bg-card/80 backdrop-blur-sm border border-border/50 rounded-lg hover:bg-card hover:shadow-md hover:scale-105 transition-all duration-200 text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{countryInfo?.flag || '🏳️'}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                              {country}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {countryInfo?.name || country}
                            </div>
                          </div>
                        </div>

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-primary/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default view: show region cards
  return (
    <div className="min-h-screen relative overflow-hidden">
      <VideoBackground
        icon={Globe}
        title="Choose your location"
        description="Select your region to discover available Shift4 products and solutions."
        showOverlay={false}
        theme={actualTheme}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-slate-900/20 overflow-y-auto">
        <div className="relative z-10 min-h-screen pt-24 pb-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-fade-in">
              <div className="space-y-6">
                {/* Back Button - Content Aligned */}
                <div className="mb-6">
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
                  >
                    <ChevronDown className="w-4 h-4 rotate-90" />
                    Back to home
                  </button>
                </div>
                {/* Region Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.keys(REGION_COUNTRY_MAP).map(regionName => {
                    const countriesInRegion = REGION_COUNTRY_MAP[regionName].filter(country =>
                      options.countries.includes(country)
                    );
                    const hasProducts = countriesInRegion.length > 0;

                    if (!hasProducts) return null;

                    const maxCountriesShown = 5;
                    const visibleCountries = countriesInRegion.slice(0, maxCountriesShown);
                    const hasMoreCountries = countriesInRegion.length > maxCountriesShown;

                    return (
                      <div
                        key={regionName}
                        onClick={() => handleRegionClick(regionName)}
                        className="group relative p-4 bg-card/80 backdrop-blur-sm border border-border/50 rounded-lg hover:bg-card hover:shadow-md hover:scale-105 transition-all duration-200 text-left cursor-pointer"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="text-2xl">{getRegionEmoji(regionName)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                {regionName}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {countriesInRegion.length}{' '}
                                {countriesInRegion.length === 1 ? 'country' : 'countries'}
                              </div>
                            </div>
                          </div>

                          {/* Direct country buttons */}
                          <div className="flex flex-wrap gap-1.5">
                            {visibleCountries.map(country => {
                              const countryInfo = COUNTRIES.find(c => c.code === country);
                              return (
                                <button
                                  key={country}
                                  onClick={e => handleCountryClick(country, e)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground rounded border hover:border-primary/50 transition-all duration-150"
                                  title={countryInfo?.name || country}
                                >
                                  <span className="text-xs">{countryInfo?.flag || '🏳️'}</span>
                                  <span className="text-xs font-medium">{country}</span>
                                </button>
                              );
                            })}
                            {hasMoreCountries && (
                              <div className="inline-flex items-center px-2 py-1 text-xs text-muted-foreground">
                                <span
                                  title={`+${countriesInRegion.length - maxCountriesShown} more countries`}
                                >
                                  +{countriesInRegion.length - maxCountriesShown} more
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-primary/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
                      </div>
                    );
                  })}
                </div>

                {/* Direct country selection dropdown */}
                <div className="text-center">
                  <div className="text-sm text-white/60 mb-3">Or select country directly:</div>
                  <div className="max-w-xs mx-auto">
                    <select
                      onChange={e => {
                        const country = e.target.value;
                        if (country) {
                          handleCountryClick(country);
                        }
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-white/90 backdrop-blur-sm text-gray-900 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                    >
                      <option value="">Select a country...</option>
                      {options.countries.map(country => {
                        const countryInfo = COUNTRIES.find(c => c.code === country);
                        return (
                          <option key={country} value={country}>
                            {countryInfo?.flag || '🏳️'} {countryInfo?.name || country}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;
